/**
 * The column model shared by every roster table (BON-538).
 *
 * Generalised from `workshops/overview/roster-columns.ts`, which proved the
 * shape: a table is described ONCE as an array, and the header cells, the body
 * cells and the CSS grid track list are all derived from that same filtered
 * array. Hiding a column is then a filter, and a track left with no cell behind
 * it is structurally unavailable rather than something to remember.
 *
 * Generic over the row because the two callers have nothing else in common.
 */
import type { ReactNode } from 'react';

export interface RosterColumnDef<Row> {
  id: string;
  label: string;
  /** One CSS grid track, e.g. `'64px'` or `'minmax(160px,1.4fr)'`. */
  width: string;
  align?: 'right';
  /** Drawn quietly, so it does not compete with the names being scanned. */
  muted?: boolean;
  render: (row: Row) => ReactNode;
}

/** The grid's track list — one track per visible column, in order. */
export const gridTemplateFor = <Row>(columns: readonly RosterColumnDef<Row>[]): string =>
  columns.map((column) => column.width).join(' ');

/** Shared cell metrics, so header and body sit on the same rhythm. */
export const ROSTER_CELL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  padding: '0 8px',
  // The roster is a grid rather than a `<table>`, so it misses `.nb-tbl`'s base
  // size and would inherit the page's instead. 13px is what the design uses.
  fontSize: 13,
} as const;
